<script setup>
import ToastComponent from '@/components/ToastComponent.vue';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, onMounted, reactive, ref, watch } from 'vue';

const props = defineProps({
    contractType: {
        type: String,
        default: 'SHORT'
    },
    validMsg: {
        type: Object,
        default: () => ({})
    },
    requiredList: {
        type: [Array, Object],
        default: () => []
    },
    readonlyList: {
        type: [Array, Object],
        default: () => []
    },
    hideList: {
        type: [Array, Object],
        default: () => []
    },
    additionalData: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['update']);
const toastComponentRef = ref(null);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};
const isReadonlyMap = ref({});
const isReservation = ref('N');
const signRef = ref(null);
const initialData = {
    contract_term: '',
    significant: '',
    customer_accident_vehicle: '',
    contract_document: '',
    customer_signature: '',
    reservation_alarm: 'N',
    payment_alarm: 'N',
    referral_alarm: 'N'
};

const data = reactive({ ...initialData });

const initialValidMessage = {
    contract_term: '',
    significant: '',
    customer_accident_vehicle: '',
    contract_document: '',
    customer_signature: ''
};
const validationMessage = reactive({ ...initialValidMessage });
const allowedKeys = Object.keys(initialData);
const requiredData = [];
const allowedSet = new Set(allowedKeys);

const requiredKeys = computed(() => {
    const res = props.requiredList;
    if (Array.isArray(res)) return res.filter((k) => allowedSet.has(k));
    if (res && typeof res === 'object') {
        return Object.keys(res).filter((k) => allowedSet.has(k) && !!res[k]);
    }
    return [];
});

const requiredKeySet = computed(() => new Set(requiredKeys.value));
const isRequired = (key) => requiredKeySet.value.has(key);

const propsReadonlyEnforced = computed(() => {
    const res = props.readonlyList;
    if (Array.isArray(res)) return new Set(res);
    if (res && typeof res === 'object') return new Set(Object.keys(res).filter((key) => !!res[key]));
    return new Set();
});
const propsReadonlyControlled = computed(() => {
    const res = props.readonlyList;
    if (Array.isArray(res)) return new Set(res);
    if (res && typeof res === 'object') return new Set(Object.keys(res));
    return new Set();
});

const allReadonly = ref(false);
const setAllReadonly = (val = true) => {
    allReadonly.value = !!val;
};

const internalReadonlyKeys = computed(() => {
    const set = new Set();
    Object.entries(isReadonlyMap.value).forEach(([key, val]) => {
        if (val && allowedSet.has(key)) set.add(key);
    });
    if (allReadonly.value) allowedKeys.forEach((k) => set.add(k));
    return set;
});

const finalReadonlySet = computed(() => {
    const result = new Set(propsReadonlyEnforced.value);
    internalReadonlyKeys.value.forEach((key) => {
        if (!propsReadonlyControlled.value.has(key)) result.add(key);
    });
    return result;
});
const isFieldReadonly = (key) => finalReadonlySet.value.has(key);

const normalizeHideMap = (src) => {
    let v = src;
    if (typeof v === 'string') {
        try {
            v = JSON.parse(v);
        } catch {
            v = undefined;
        }
    }
    const map = {};
    if (Array.isArray(v)) {
        for (const k of v) if (allowedSet.has(k)) map[k] = true;
    } else if (v && typeof v === 'object') {
        for (const [k, val] of Object.entries(v)) {
            if (allowedSet.has(k)) map[k] = !!val;
        }
    }
    return map;
};

const propsHideMap = computed(() => normalizeHideMap(props.hideList));

const internalHideSet = computed(() => {
    const set = new Set();
    const ctype = props.contractType;

    if (ctype !== 'MID' && ctype !== 'LONG') set.add('payment_alarm');

    if (ctype !== 'INSU') set.add('customer_accident_vehicle');

    return set;
});

const signHeight = computed(() => (isHidden('payment_alarm') ? '8.8rem' : '14.6rem'));
const docsHeight = computed(() => (isHidden('payment_alarm') ? '8.6rem' : '14.5rem'));

const finalHideSet = computed(() => {
    const out = new Set(internalHideSet.value);
    for (const [k, v] of Object.entries(propsHideMap.value)) {
        if (v === false) out.delete(k);
        else out.add(k);
    }
    return out;
});

const isHidden = (key) => finalHideSet.value.has(key);

const isEffectivelyRequired = (key) => requiredKeySet.value.has(key) && !isFieldReadonly(key) && !isHidden(key);

const resetData = () => {
    Object.assign(data, initialData);
    Object.assign(validationMessage, initialValidMessage);
};

const getData = async () => {
    const payload = {};
    for (const key of allowedKeys) {
        if (isHidden(key) || isFieldReadonly(key)) continue;

        if (key === 'customer_signature') {
            if (signRef.value?.getSignatureAsMultipart) {
                payload.customer_signature = await signRef.value.getSignatureAsMultipart();
            } else {
                const val = data.customer_signature;
                payload.customer_signature = val instanceof Blob || typeof val === 'string' ? val : null;
            }
        } else {
            payload[key] = data[key];
        }
    }

    return payload;
};

const setadditionalData = (newVal) => {
    if (!newVal) return;
    const payload = newVal.contract_data ?? newVal;

    console.log('들어온 추가 데이터!!!: ', newVal);

    Object.entries(payload).forEach(([key, val]) => {
        if (allowedKeys.includes(key) && key !== 'reservation_attachment_list') {
            data[key] = val;
        }
    });

    // 첨부파일 처리
    if (Array.isArray(payload.reservation_attachment_list)) {
        const grouped = {};
        payload.reservation_attachment_list.forEach((file) => {
            const cat = file.attachment_category;
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push({
                fileid: file.attachment_id,
                filename: file.attachment_filename,
                name: file.original_filename,
                uploaded: true,
                delete: false
            });
        });

        if (grouped.contract_document) {
            data.contract_document = grouped.contract_document;
        }
        if (grouped.customer_signature) {
            data.customer_signature = grouped.customer_signature;
        }
        if (grouped.customer_accident_vehicle) {
            data.customer_accident_vehicle = grouped.customer_accident_vehicle;
        }
    }

    // console.log('조회된 기타 데이터', data);
};

const checkRequired = () => {
    Object.keys(validationMessage).forEach((k) => (validationMessage[k] = ''));
    let result = true;

    const keysToCheck = new Set(requiredKeys.value.filter((key) => allowedKeys.includes(key) && isEffectivelyRequired(key)));

    for (const key of keysToCheck) {
        const val = data[key];
        const isEmpty = val === '' || val === null || val === undefined;
        const isIdInvalid = key.endsWith('_id') && (isEmpty || Number(val) === 0);
        if (isEmpty || isIdInvalid) {
            if (key in validationMessage) validationMessage[key] = '필수입력입니다.';
            result = false;
        }
    }
    return result;
};

const setErrorMessage = (payload, reset = false) => {
    if (!payload) return;

    if (reset) {
        Object.keys(validationMessage).forEach((k) => (validationMessage[k] = ''));
    }

    const collectPairs = (node, out = []) => {
        if (node == null) return out;

        if (Array.isArray(node)) {
            node.forEach((item) => collectPairs(item, out));
            return out;
        }

        if (typeof node === 'object') {
            for (const [k, v] of Object.entries(node)) {
                if (v && typeof v === 'object' && !Array.isArray(v)) {
                    collectPairs(v, out);
                } else {
                    out.push([k, v]);
                }
            }
            return out;
        }

        return out;
    };

    const pairs = collectPairs(payload);

    for (const [rawKey, rawVal] of pairs) {
        let key = String(rawKey).replace(/^contract_data\./, '');
        if (!(key in validationMessage)) continue;

        let msg = rawVal;
        if (Array.isArray(msg)) msg = msg.filter(Boolean).join(', ');
        if (msg == null) msg = '';

        validationMessage[key] = String(msg);
    }
};

onMounted(() => {
    //resetData();
    // if (props.additionalData && Object.keys(props.additionalData).length > 0) {
    //     setadditionalData(props.additionalData);
    // }
});

watch(
    () => props.additionalData,
    (newVal) => {
        if (!newVal || typeof newVal !== 'object') return;
        setadditionalData(newVal);
    },
    { immediate: true, deep: false }
);

watch(
    () => props.readonlyList,
    () => {
        Object.keys(validationMessage).forEach((key) => {
            if (isFieldReadonly(key)) validationMessage[key] = '';
        });
        // checkRequired();
    },
    { immediate: false, deep: false }
);

watch(
    () => props.hideList,
    () => {
        Object.keys(validationMessage).forEach((key) => {
            if (isHidden(key)) validationMessage[key] = '';
        });
    },
    { immediate: false, deep: false }
);

watch(
    () => props.validMsg,
    (newVal) => {
        if (!newVal || typeof newVal !== 'object') {
            console.error('Invalid Error data');
            return;
        }
        setErrorMessage(newVal, true);
        // Object.keys(newVal).forEach((key) => {
        //     if (key.startsWith('contract_data.')) {
        //         const pureKey = key.replace('contract_data.', '');
        //         if (pureKey in validationMessage) {
        //             validationMessage[pureKey] = newVal[key];
        //         }
        //     } else {
        //         if (key in validationMessage) {
        //             validationMessage[key] = newVal[key];
        //         }
        //     }
        // });
    },
    { immediate: true }
);

watch(
    () => props.contractType,
    () => {
        Object.keys(validationMessage).forEach((key) => {
            if (isHidden(key)) validationMessage[key] = '';
        });
    }
);

defineExpose({
    getData,
    resetData,
    setadditionalData,
    checkRequired, // 필수입력 체크
    setAllReadonly, // 전체 readonly
    setErrorMessage
});
</script>

<template>
    <ToastComponent ref="toastComponentRef" />
    <div class="section-box base-text">
        <h2 class="section-title py-4">기타 정보</h2>
        <div class="section-body">
            <div class="section-left">
                <div class="section-row">
                    <div v-if="!isHidden('significant')" class="input-box w-[100%]">
                        <customtextbox
                            inputId="significant"
                            :placeholder="'특이사항을 입력하세요.'"
                            v-model="data.significant"
                            label="특이사항"
                            :height="'8rem'"
                            :errmsg="validationMessage.significant"
                            :readonly="isFieldReadonly('significant')"
                            :required="isEffectivelyRequired('significant')"
                        />
                    </div>
                </div>
                <div v-if="!isHidden('contract_document')" class="section-row items-center">
                    <div class="input-box w-full">
                        <multiuploadbox
                            inputId="contract_document"
                            v-model="data.contract_document"
                            :errmsg="validationMessage.contract_document"
                            label="계약서류"
                            :height="docsHeight"
                            :category="'contract_document'"
                            :readonly="isFieldReadonly('contract_document')"
                            :required="isEffectivelyRequired('contract_document')"
                        />
                    </div>
                </div>
                <div class="section-row items-center">
                    <div v-if="!isHidden('customer_accident_vehicle')" class="input-box w-full">
                        <multiuploadbox
                            inputId="customer_accident_vehicle"
                            v-model="data.customer_accident_vehicle"
                            :errmsg="validationMessage.customer_accident_vehicle"
                            label="사고차량이미지"
                            :height="'7.3rem'"
                            :category="'customer_accident_vehicle'"
                            :readonly="isFieldReadonly('customer_accident_vehicle')"
                            :required="isEffectivelyRequired('customer_accident_vehicle')"
                        />
                    </div>
                </div>
            </div>
            <div class="section-right">
                <div class="section-row items-center">
                    <div v-if="!isHidden('contract_term')" class="input-box w-full">
                        <contractterm
                            inputId="contract_term"
                            v-model="data.contract_term"
                            :height="'8rem'"
                            label="기타 계약사항"
                            :placeholder="'기타 계약사항 선택 및 입력하세요.'"
                            :readonly="isFieldReadonly('contract_term')"
                            :required="isEffectivelyRequired('contract_term')"
                            :errmsg="validationMessage.contract_term"
                        />
                    </div>
                </div>
                <div class="section-row items-stretch grid grid-cols-[18rem,1fr] gap-3">
                    <div class="flex flex-col gap-3">
                        <div v-if="!isHidden('reservation_alarm')" class="input-box">
                            <switchbox
                                inputId="reservation_alarm"
                                v-model="data.reservation_alarm"
                                label="렌트알림"
                                :errmsg="validationMessage.reservation_alarm"
                                :readonly="isFieldReadonly('reservation_alarm')"
                                :required="isEffectivelyRequired('reservation_alarm')"
                                :returnYorN="true"
                            />
                        </div>

                        <div v-if="!isHidden('referral_alarm')" class="input-box">
                            <switchbox
                                inputId="referral_alarm"
                                v-model="data.referral_alarm"
                                label="소개처 알림"
                                :errmsg="validationMessage.referral_alarm"
                                :readonly="isFieldReadonly('referral_alarm')"
                                :required="isEffectivelyRequired('referral_alarm')"
                                :returnYorN="true"
                            />
                        </div>

                        <div v-if="!isHidden('payment_alarm')" class="input-box">
                            <switchbox
                                inputId="payment_alarm"
                                v-model="data.payment_alarm"
                                label="월/장기렌트 납부 알림"
                                :errmsg="validationMessage.payment_alarm"
                                :readonly="isFieldReadonly('payment_alarm')"
                                :required="isEffectivelyRequired('payment_alarm')"
                                :returnYorN="true"
                            />
                        </div>
                    </div>

                    <div v-if="!isHidden('customer_signature')" class="input-box w-full h-full">
                        <sign ref="signRef" inputId="customer_signature" v-model="data.customer_signature" label="서명" :height="signHeight" :readonly="isFieldReadonly('customer_signature')" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style src="@/assets/css/form-common.css"></style>
